// src/components/dashboard/PortfolioHistoryChart.jsx
// Sparkline (collapsed) and full history chart (expanded) for portfolio value over time.

import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceDot,
} from 'recharts';
import { supabase } from '../../lib/supabaseClient';
import { getSnapshotHistory } from '../../lib/portfolioSnapshots';

const GREEN   = '#3ddc84';
const BG      = '#060a0f';
const SURFACE = '#0d1117';
const BORDER  = '#1c2333';
const MUTED   = '#8b949e';
const ERROR_COLOR = '#ff6b35';

const EVENT_COLORS = {
  holding_added:   '#3ddc84',
  holding_removed: '#ff6b35',
  onboarding:      '#00c8ff',
  manual_update:   '#ffd700',
  market_close:    MUTED,
  midnight_scheduled: MUTED,
};

const RANGES = [
  { label: '7D',  days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: 'All', days: null },
];

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatValue(v) {
  if (v == null) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: 6,
      padding: '8px 12px',
      fontFamily: 'var(--font-mono)',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: GREEN, marginBottom: 2 }}>
        {formatValue(d.value)}
      </div>
      <div style={{ fontSize: 10, color: MUTED, marginBottom: 2 }}>
        {formatDate(d.snapshot_at)}
      </div>
      {d.trigger_type && (
        <div style={{ fontSize: 9, color: EVENT_COLORS[d.trigger_type] ?? MUTED, letterSpacing: '.06em' }}>
          {d.trigger_type.replace(/_/g, ' ').toUpperCase()}
          {d.notes ? ` · ${d.notes}` : ''}
        </div>
      )}
    </div>
  );
}

export default function PortfolioHistoryChart() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [range, setRange] = useState('30D');

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !mounted) return;
        const data = await getSnapshotHistory(user.id, 90);
        if (mounted) setHistory(data ?? []);
      } catch {}
      if (mounted) setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, []);

  const filteredData = (() => {
    const selected = RANGES.find(r => r.label === range);
    if (!selected?.days) return history;
    const cutoff = new Date(Date.now() - selected.days * 86400_000).toISOString();
    return history.filter(d => d.snapshot_at >= cutoff);
  })();

  const displayData = expanded ? filteredData : history.slice(-30);

  const eventDots = displayData.filter(d =>
    d.trigger_type === 'holding_added' || d.trigger_type === 'holding_removed'
  );

  const firstVal = displayData[0]?.value;
  const lastVal  = displayData[displayData.length - 1]?.value;
  const delta    = firstVal && lastVal ? lastVal - firstVal : null;
  const deltaPos = delta !== null && delta >= 0;

  if (loading) {
    return (
      <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: MUTED }}>
          Loading history…
        </span>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div style={{ padding: '14px 0 4px', borderTop: `1px solid ${BORDER}`, marginTop: 12 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: MUTED, letterSpacing: '.06em' }}>
          No history yet. Your portfolio value will be tracked from today.
        </div>
      </div>
    );
  }

  return (
    <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 16, paddingTop: 12 }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: MUTED, letterSpacing: '.1em' }}>
          PORTFOLIO HISTORY
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {delta !== null && (
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
              color: deltaPos ? GREEN : ERROR_COLOR,
            }}>
              {deltaPos ? '+' : ''}{formatValue(delta)}
            </span>
          )}
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'transparent', border: 'none',
              color: GREEN, fontFamily: 'var(--font-mono)',
              fontSize: 9, cursor: 'pointer', letterSpacing: '.06em',
            }}
          >
            {expanded ? '↙ Collapse' : '↗ View full history'}
          </button>
        </div>
      </div>

      {/* Range selector — only in expanded mode */}
      {expanded && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {RANGES.map(r => (
            <button
              key={r.label}
              onClick={() => setRange(r.label)}
              style={{
                background: range === r.label ? 'rgba(61,220,132,.12)' : 'transparent',
                border: `1px solid ${range === r.label ? 'rgba(61,220,132,.3)' : BORDER}`,
                borderRadius: 4,
                color: range === r.label ? GREEN : MUTED,
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                padding: '3px 10px',
                cursor: 'pointer',
                letterSpacing: '.06em',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}

      {/* Chart */}
      <div style={{ height: expanded ? 280 : 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={displayData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            {expanded && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,.04)"
                vertical={false}
              />
            )}
            {expanded && (
              <XAxis
                dataKey="snapshot_at"
                tickFormatter={formatDate}
                tick={{ fontFamily: 'var(--font-mono)', fontSize: 9, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
            )}
            {expanded && (
              <YAxis
                tickFormatter={v => formatValue(v)}
                tick={{ fontFamily: 'var(--font-mono)', fontSize: 9, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                width={60}
                domain={['auto', 'auto']}
              />
            )}
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(61,220,132,.2)', strokeWidth: 1 }} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={GREEN}
              strokeWidth={expanded ? 2 : 1.5}
              dot={false}
              activeDot={{ r: 4, fill: GREEN, strokeWidth: 0 }}
            />
            {/* Event dots for holding add/remove */}
            {expanded && eventDots.map((d, i) => (
              <ReferenceDot
                key={i}
                x={d.snapshot_at}
                y={d.value}
                r={4}
                fill={EVENT_COLORS[d.trigger_type]}
                stroke={BG}
                strokeWidth={1}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* First / last labels in collapsed mode */}
      {!expanded && firstVal != null && lastVal != null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: MUTED }}>
            {formatValue(firstVal)}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: GREEN, fontWeight: 700 }}>
            {formatValue(lastVal)}
          </span>
        </div>
      )}
    </div>
  );
}
